'use client'

import Link from 'next/link'
import { GraduationCap, Upload, Monitor, ArrowRight } from 'lucide-react'

export default function Home() {
  return (
    <div className="min-h-screen bg-transparent dir-rtl">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB] shadow-sm" role="navigation" aria-label="Main navigation">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#168A63] rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">ص</span>
              </div>
              <h1 className="text-2xl font-bold text-[#1F2937]" aria-label="SadApp - Educational Platform">
                SADA
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link
                href="/login"
                className="px-6 py-3 text-[#168A63] font-semibold hover:bg-[#EAF7F1] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#168A63]"
                aria-label="Login to your account"
              >
                تسجيل الدخول
              </Link>
              <Link
                href="/register"
                className="px-6 py-3 bg-[#168A63] text-white font-semibold rounded-lg hover:bg-[#0F6B4A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#168A63]"
                aria-label="Create a new account"
              >
                إنشاء حساب
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="mx-auto">
        {/* Hero Section */}
        <div className="relative overflow-hidden mb-20 h-[600px] bg-[#168A63]">
          <video
            src="/hero.mp4"
            controls={false}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-12">
            <div className="text-white max-w-2xl">
              <h2 className="text-4xl font-bold mb-4">منصة تعليمية متاحة للجميع</h2>
              <p className="text-lg opacity-90 mb-6">حوّل المحتوى التعليمي إلى تجربة صوتية تفاعلية</p>
            </div>
          </div>
        </div>

        {/* User Type Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20 text-right px-6">
          {/* Students Card */}
          <div className="card hover:shadow-lg transition-all hover:-translate-y-1" role="region" aria-labelledby="students-title">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-14 h-14 bg-[#EAF7F1] rounded-xl flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-[#168A63]" />
              </div>
              <h3 id="students-title" className="text-2xl font-bold text-[#1F2937]">
                للطلبة
              </h3>
            </div>
            <p className="text-[#6B7280] mb-6 leading-relaxed">
              استمع إلى محتواك التعليمي في أي وقت ومكان. منصة سادا تجعل الدراسة أكثر مرونة وسهولة.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 text-[#168A63] font-semibold hover:gap-3 transition-all"
            >
              ابدأ الآن
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Professors Card */}
          <div className="card hover:shadow-lg transition-all hover:-translate-y-1" role="region" aria-labelledby="professors-title">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-14 h-14 bg-[#EAF7F1] rounded-xl flex items-center justify-center">
                <Upload className="w-7 h-7 text-[#168A63]" />
              </div>
              <h3 id="professors-title" className="text-2xl font-bold text-[#1F2937]">
                للأساتذة
              </h3>
            </div>
            <p className="text-[#6B7280] mb-6 leading-relaxed">
              شارك معرفتك مع الطلاب بطريقة جديدة. ارفع محتواك وتابع تفاعل الطلاب في الوقت الفعلي.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 text-[#168A63] font-semibold hover:gap-3 transition-all"
            >
              انضم كأستاذ
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Accessibility Card */}
          <div className="card hover:shadow-lg transition-all hover:-translate-y-1" role="region" aria-labelledby="accessibility-title">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-14 h-14 bg-[#EAF7F1] rounded-xl flex items-center justify-center">
                <Monitor className="w-7 h-7 text-[#168A63]" />
              </div>
              <h3 id="accessibility-title" className="text-2xl font-bold text-[#1F2937]">
                للجميع
              </h3>
            </div>
            <p className="text-[#6B7280] mb-6 leading-relaxed">
              تصميم متوافق مع معايير إمكانية الوصول العالمية. تجربة مستخدم سلسة لجميع الفئات.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 text-[#168A63] font-semibold hover:gap-3 transition-all"
            >
              اكتشف المزيد
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* CTA Section */}
        <div className="card bg-[#EAF7F1] border-2 border-[#168A63] text-center py-16">
          <h3 className="text-3xl font-bold text-[#1F2937] mb-4">
            انضم إلى مجتمعنا التعليمي
          </h3>
          <p className="text-[#6B7280] mb-8 max-w-2xl mx-auto text-lg">
            ابدأ رحلتك التعليمية اليوم مع منصة سادا. التسجيل مجاني وسريع.
          </p>
          <Link
            href="/register"
            className="inline-block px-10 py-4 bg-[#168A63] text-white text-lg font-semibold rounded-lg hover:bg-[#0F6B4A] transition-colors focus:outline-none focus:ring-4 focus:ring-[#168A63]"
            aria-label="Get started with SadApp"
          >
            إنشاء حساب مجاني
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E5E7EB] mt-16 py-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-[40px] text-center text-[#6B7280]">
          <p>© 2026 SADA. جميع الحقوق محفوظة.</p>
        </div>
      </footer>
    </div>
  )
}
